import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { BadgeCheck, Flag, Clock, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => {
  const t = localStorage.getItem('eventism_token');
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

export function VenueClaimWidget({ city, venueSlug }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setStatus((await axios.get(`${API}/venue-profiles/${city}/${venueSlug}/claim-status`, auth())).data); }
    catch { /* ignore */ }
  }, [city, venueSlug]);
  useEffect(() => { load(); }, [load]);

  if (!status) return null;

  if (status.claimed) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[11px]" data-testid="venue-claimed-badge">
          <BadgeCheck className="h-3 w-3 mr-1" /> Verified
        </Badge>
        {status.i_manage && (
          <Button size="sm" variant="outline" className="h-7 text-[11px] border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
            onClick={() => navigate(`/venue-manage/${city}/${venueSlug}`)} data-testid="venue-manage-btn">
            <Pencil className="h-3 w-3 mr-1" /> Manage this page
          </Button>
        )}
      </div>
    );
  }
  if (status.my_pending_claim) {
    return (
      <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[11px]" data-testid="venue-claim-pending">
        <Clock className="h-3 w-3 mr-1" /> Your claim is under review
      </Badge>
    );
  }

  const claim = async () => {
    if (!localStorage.getItem('eventism_token')) {
      toast.info('Sign in first, then claim your venue');
      return navigate('/partner-hub');
    }
    const message = window.prompt('Tell us briefly how you own or manage this venue (helps us verify fast):') || '';
    setBusy(true);
    try {
      await axios.post(`${API}/venue-profiles/${city}/${venueSlug}/claim`, { message }, auth());
      toast.success('Claim submitted — we review within 1 business day');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not submit claim'); }
    finally { setBusy(false); }
  };

  return (
    <Button size="sm" variant="outline" className="h-7 text-[11px] border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"
      disabled={busy} onClick={claim} data-testid="venue-claim-btn">
      <Flag className="h-3 w-3 mr-1" /> This is my venue — claim it
    </Button>
  );
}
